import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AlertCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { createGuestSupportApi } from '../services/adapters/requesterSupportApi';
import type { RequesterTicketDetail } from '../services/supportApi';
import ThreadView from '../components/support/ThreadView';
import RequesterPanel from '../components/support/RequesterPanel';
import StatusBadge from '../components/support/StatusBadge';
import { GUEST_MERGED, requesterBadgeLabels } from '../components/support/displayStatus';
import {
  ACCEPTED_TYPES,
  GUEST_STATUS_LABELS,
  MAX_FILES,
  MAX_FILE_SIZE,
  REOPEN_WINDOW_DAYS,
} from '../components/support/constants';

// Pagina pubblica della richiesta ospite (/richiesta/:id): unica credenziale il
// token HMAC del link email, tenuto solo in memoria.
const GuestTicketPage: React.FC = () => {
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // Il token vive SOLO in memoria (una ref): mai in localStorage/cookie e nemmeno
  // nell'URL oltre il primo istante. Subito dopo averlo letto ripuliamo la query
  // string con replaceState, così non finisce nel page_view di Google Analytics,
  // nella cronologia del browser, nel Referer o in uno screenshot.
  const tokenRef = useRef<string>(searchParams.get('t') || '');
  const [hasToken, setHasToken] = useState<boolean>(!!tokenRef.current);
  useEffect(() => {
    if (!tokenRef.current) return;
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch {
      /* ambienti senza history API: il token resta nell'URL, la pagina funziona comunque */
    }
  }, []);
  const token = tokenRef.current;
  // L'adapter ospite dipende dal token (che non cambia mai in pagina): memo +
  // ref, cosi il caricamento non dipende dall'identita dell'oggetto.
  const api = useMemo(() => createGuestSupportApi(token), [token]);
  const apiRef = useRef(api);
  apiRef.current = api;

  const [ticket, setTicket] = useState<RequesterTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [replyBody, setReplyBody] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [isRating, setIsRating] = useState(false);

  const loadTicket = useCallback(async (silent = false) => {
    if (!id || !token) { setHasToken(false); setLoadError(true); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const data = await apiRef.current.getTicket(id);
      setTicket(data);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  const handleFilesSelected = (selected: File[]) => {
    const valid: File[] = [];
    for (const f of selected) {
      if (!ACCEPTED_TYPES.includes(f.type)) { toast.error(`Tipo file non ammesso: ${f.name}`); continue; }
      if (f.size > MAX_FILE_SIZE) { toast.error(`"${f.name}" supera i 10MB`); continue; }
      valid.push(f);
    }

    setReplyFiles((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > MAX_FILES) {
        toast.error(`Massimo ${MAX_FILES} file`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removeFile = (index: number) => setReplyFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSend = async () => {
    if (!id || (!replyBody.trim() && replyFiles.length === 0)) {
      toast.error('Scrivi un messaggio o allega un file');
      return;
    }
    setIsSending(true);
    try {
      await api.reply(id, replyBody.trim(), replyFiles.length > 0 ? replyFiles : undefined);
      setReplyBody('');
      setReplyFiles([]);
      toast.success('Messaggio inviato!');
      await loadTicket(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Errore durante l\'invio');
    } finally {
      setIsSending(false);
    }
  };

  const handleReopen = async () => {
    if (!id) return;
    setIsReopening(true);
    try {
      await api.reopen(id);
      toast.success('Richiesta riaperta');
      await loadTicket(true);
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.warning(error.response?.data?.error || 'Finestra di riapertura scaduta');
      } else {
        toast.error(error.response?.data?.error || 'Errore durante la riapertura');
      }
    } finally {
      setIsReopening(false);
    }
  };

  const handleRate = async (stars: number, comment: string) => {
    if (!id) return;
    if (stars < 1) { toast.error('Seleziona una valutazione'); return; }
    setIsRating(true);
    try {
      await api.rate(id, stars, comment);
      toast.success('Grazie per la tua valutazione!');
      await loadTicket(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Errore durante l\'invio della valutazione');
    } finally {
      setIsRating(false);
    }
  };

  const handleOpenAttachment = async (storedName: string, displayName?: string) => {
    if (!ticket) return;
    try {
      await api.openAttachment(ticket._id, storedName, displayName);
    } catch {
      toast.error('Impossibile aprire l\'allegato');
    }
  };

  // --- Render ---

  const noIndex = (
    <Helmet>
      <title>La tua richiesta | UNASCA</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        {noIndex}
        <div className="w-8 h-8 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError || !ticket) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 px-4 text-center">
        {noIndex}
        <AlertCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {hasToken
            ? 'Richiesta non trovata o link scaduto: controlla l\'email ricevuta'
            : 'Link non valido: riapri il link dall\'email'}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
          {hasToken
            ? 'Il collegamento alla richiesta è personale. Se il problema persiste, scrivici dalla pagina contatti indicando il numero della tua richiesta.'
            : 'Per motivi di sicurezza il codice di accesso non resta nell\'indirizzo: ricaricando la pagina occorre ripartire dal link che trovi nell\'email di conferma.'}
        </p>
      </div>
    );
  }

  // Riapertura: SOLO da 'risolto' entro la finestra da resolvedAt (speculare a
  // `canReopen` del backend). Una richiesta chiusa e definitiva.
  const canReopenNow = ticket.status === 'risolto' && !!ticket.resolvedAt &&
    (Date.now() - new Date(ticket.resolvedAt).getTime()) <= REOPEN_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const channelName = ticket.canale || 'UNASCA';

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      {noIndex}
      {/* Intestazione */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-gray-400 dark:text-gray-500">#{ticket.ticketNumber}</span>
            {/* Richiesta unita a un'altra: «Unita» al posto di «Chiusa» (stessa concordanza). */}
            <span title={requesterBadgeLabels(ticket, GUEST_STATUS_LABELS, GUEST_MERGED).title}>
              <StatusBadge status={ticket.status} labels={requesterBadgeLabels(ticket, GUEST_STATUS_LABELS, GUEST_MERGED).labels} dot />
            </span>
          </div>
          <h1 className="text-base font-bold text-gray-900 dark:text-white sm:text-lg">{ticket.subject}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{channelName}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <ThreadView
          variant="requester"
          messages={ticket.publicThread}
          onOpenAttachment={handleOpenAttachment}
          emptyText="Nessun messaggio nella richiesta"
          mineAuthorType="guest"
          channelName={channelName}
        />

        <RequesterPanel
          ticket={ticket}
          replyBody={replyBody}
          onReplyBodyChange={setReplyBody}
          replyFiles={replyFiles}
          onAddFiles={handleFilesSelected}
          onRemoveFile={removeFile}
          onSend={handleSend}
          isSending={isSending}
          canReopen={canReopenNow}
          isReopening={isReopening}
          onReopen={handleReopen}
          onRate={handleRate}
          isRating={isRating}
          labels={{ closed: 'Questa richiesta è chiusa', reopen: 'Riapri richiesta' }}
        />

        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
          Questa pagina è personale: il link ricevuto via email non va condiviso.
        </p>
      </div>
    </div>
  );
};

export default GuestTicketPage;
